import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import { generateRuleFromTransaction } from '@/lib/bankTransactionRuleEngine';
import { classifyTransaction, type ClassificationResult } from '@/lib/transactionClassifier';
import { smartGroupTransactions } from '@/lib/transactionGrouping';
import { SubcategoryCombobox, DetailSourceCombobox } from './SubcategoryCombobox';
import { 
  Sparkles, 
  ChevronLeft, 
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Split, 
  Loader2,
  ArrowRight,
  Brain,
  Eye,
  EyeOff,
  Layers,
  Zap,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  CreditCard,
  List,
  Calendar,
  Upload
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface BankAccountsPhase2Step3Props {
  onNext: () => void;
  onPrevious: () => void;
  accountId: string | null;
}

interface Transaction {
  id: string;
  transaction_date: string;
  amount: number;
  description: string;
  merchant_name?: string;
  category_type?: 'revenue' | 'expenses' | 'balance_sheet' | null;
  subcategory?: string | null;
  detail_source?: string | null;
  location_id?: string;
  period_flag: string;
  account_id?: string; // Optional to match transactionGrouping interface
  account_name?: string; // Added for display
  is_credit_card?: boolean; // Flag for credit card accounts (inverted sign convention)
}

interface TransactionGroup {
  descriptionPattern: string;
  transactions: Transaction[];
  suggestion?: ClassificationResult;
  isTrained: boolean;
  isRevenue?: boolean; // true if positive amounts (revenue), false if negative (expenses)
}

export const BankAccountsPhase2Step3 = ({ onNext, onPrevious, accountId }: BankAccountsPhase2Step3Props) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [transactionGroups, setTransactionGroups] = useState<TransactionGroup[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSimilar, setShowSimilar] = useState(false);
  const [classifying, setClassifying] = useState(false);
  
  // Ungroup functionality
  const [ungroupMode, setUngroupMode] = useState(false);
  const [selectedTransactionsForUngroup, setSelectedTransactionsForUngroup] = useState<Set<string>>(new Set());
  
  // Expanded descriptions
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  
  // Form state
  const [categoryType, setCategoryType] = useState<'revenue' | 'expenses' | 'balance_sheet'>('expenses');
  const [subcategory, setSubcategory] = useState('');
  const [detailSource, setDetailSource] = useState('');
  
  const [dataLoaded, setDataLoaded] = useState(false); // Track if initial load completed
  
  // Date range for import
  const [trainingWindowStart, setTrainingWindowStart] = useState<string>('');
  const [trainingWindowEnd, setTrainingWindowEnd] = useState<string>('');
  const [importing, setImporting] = useState(false);
  
  // Initialize date range on mount
  useEffect(() => {
    const now = new Date();
    const oneMonthAgo = new Date(now);
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const trainingStart = oneMonthAgo.toISOString().split('T')[0];
    const trainingEnd = now.toISOString().split('T')[0];
    
    setTrainingWindowStart(trainingStart);
    setTrainingWindowEnd(trainingEnd);
  }, []);

  // Default category from debit/credit rules per account type:
  // Bank Statement:  Debit = Money OUT (expenses), Credit = Money IN (revenue)
  // Credit Card:     Debit = Money IN (payments/refunds), Credit = Money OUT (purchases)
  const getDefaultCategory = (tx: Transaction): 'revenue' | 'expenses' | 'balance_sheet' => {
    if (tx.is_credit_card) {
      // CC: Credit (positive) = expense; Debit (negative) = money in → balance_sheet (payment) or revenue (refund)
      return tx.amount >= 0 ? 'expenses' : 'balance_sheet';
    }
    return tx.amount >= 0 ? 'revenue' : 'expenses';
  };

  // Load transactions on mount and when accountId changes
  // No processedRef - we WANT to reload fresh data each time
  // This ensures categorized transactions are filtered out when returning to this step
  useEffect(() => {
    loadTransactions();
  }, [accountId]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        setLoading(false);
        setDataLoaded(true);
        return;
      }

      // Build query - if accountId is null, load from all accounts
      // ONLY load TRAINING WINDOW transactions that need categorization (category_type is null)
      // Already categorized transactions (category_type is NOT null) are excluded
      // Only select essential columns to reduce data transfer
      let query = supabase
        .from('bank_transactions')
        .select('id, transaction_date, amount, description, merchant_name, category_type, subcategory, detail_source, location_id, period_flag, account_id')
        .eq('company_id', companyId)
        .eq('period_flag', 'training_window')
        .is('category_type', null);
      
      if (accountId) {
        query = query.eq('account_id', accountId);
      }

      const { data: transactionsData, error } = await query
        .order('transaction_date', { ascending: false });
      
      console.log(`Loaded ${transactionsData?.length || 0} uncategorized training_window transactions`);

      if (error) throw error;

      // If no uncategorized transactions, show empty state
      if (!transactionsData || transactionsData.length === 0) {
        setTransactionGroups([]);
        setLoading(false);
        setDataLoaded(true);
        return;
      }

      // Load account details (name and type for credit card detection)
      const accountIds = [...new Set(transactionsData.map((tx: any) => tx.account_id))];
      const { data: accountsData } = await supabase
        .from('bank_accounts')
        .select('id, account_name, account_type')
        .in('id', accountIds);

      const accountMap = new Map((accountsData || []).map((acc: any) => [acc.id, { 
        name: acc.account_name,
        type: acc.account_type,
        // Detect credit card by name or type
        isCreditCard: acc.account_type === 'credit' || 
                      acc.account_name?.toLowerCase().includes('credit') ||
                      acc.account_name?.toLowerCase().includes(' cc') ||
                      acc.account_name?.toLowerCase().endsWith(' cc')
      }]));

      // Add account names and credit card flag to transactions
      const transactions = transactionsData.map((tx: any) => {
        const accountInfo = accountMap.get(tx.account_id) || { name: 'Unknown Account', isCreditCard: false };
        return {
          ...tx,
          account_name: accountInfo.name,
          is_credit_card: accountInfo.isCreditCard,
        };
      }) as Transaction[];

      // Sort by date (most recent first)
      transactions.sort((a, b) => 
        new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime()
      );

      // Use smart grouping: keyword detection + fuzzy matching
      // When dealing with combined accounts (accountId is null), we need stricter grouping
      // to avoid grouping unrelated transactions from different accounts
      const isCombinedAccounts = accountId === null;
      const groupArray = smartGroupTransactions(transactions, isCombinedAccounts);

      // Double-check: ensure no mixed-type groups exist (defensive)
      // Use effective type per account type (Bank vs Credit Card sign convention):
      // Bank: Credit (positive) = revenue, Debit (negative) = expense
      // Credit card: Credit (positive) = expense (purchases), Debit (negative) = revenue (money in)
      const getEffectiveType = (tx: Transaction): 'revenue' | 'expense' => {
        if (tx.is_credit_card) {
          return tx.amount > 0 ? 'expense' : 'revenue'; // CC: + = purchase, - = payment/refund
        }
        return tx.amount >= 0 ? 'revenue' : 'expense'; // Bank: + = deposit, - = withdrawal
      };
      
      const validatedGroups = groupArray.filter(group => {
        if (group.transactions.length === 0) return false;
        const firstType = getEffectiveType(group.transactions[0]);
        const allSameType = group.transactions.every(tx => getEffectiveType(tx) === firstType);
        if (!allSameType) {
          console.warn('Found mixed-type group (revenue/expense), filtering out:', group.descriptionPattern);
          return false;
        }
        return true;
      }).map(group => ({
        ...group,
        isTrained: false, // Explicitly set to false - groups are only marked as trained when user saves
      }));

      setTransactionGroups(validatedGroups);
      setLoading(false);
      setDataLoaded(true);

      // Auto-classify the first transaction
      if (validatedGroups.length > 0) {
        const firstGroup = validatedGroups[0];
        const firstTx = firstGroup.transactions[0];
        
        // Check if any transaction in the group has saved values
        const savedDetailSource = firstGroup.transactions.find(t => t.detail_source)?.detail_source || null;
        const savedSubcategory = firstGroup.transactions.find(t => t.subcategory)?.subcategory || null;
        const savedCategoryType = firstGroup.transactions.find(t => t.category_type)?.category_type || null;
        
        // Use saved values if available, otherwise default
        if (savedCategoryType) {
          setCategoryType(savedCategoryType);
          setSubcategory(savedSubcategory || '');
          setDetailSource(savedDetailSource || '');
        } else {
          // Default category based on account type and amount
          // Credit cards have inverted sign convention
          setCategoryType(getDefaultCategory(firstTx));
          setSubcategory(savedSubcategory || '');
          setDetailSource(savedDetailSource || '');
          // Classify with index 0 since this is the first group
          await classifyCurrentTransaction(firstGroup, 0);
        }
      }
    } catch (error: any) {
      console.error('Error loading transactions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load transactions',
        variant: 'destructive',
      });
      setLoading(false);
      setDataLoaded(true); // Mark as loaded even on error to prevent stuck state
    }
  };

  const classifyCurrentTransaction = async (group: TransactionGroup, groupIndex?: number) => {
    if (!group.transactions[0]?.account_id) return;
    
    setClassifying(true);
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        setClassifying(false);
        return;
      }

      const tx = group.transactions[0];
      const result = await classifyTransaction({
        id: tx.id,
        transaction_date: tx.transaction_date,
        amount: tx.amount,
        description: tx.description || '',
        merchant_name: tx.merchant_name,
        account_id: tx.account_id,
        company_id: companyId,
        location_id: tx.location_id,
      });

      // Use provided index or currentIndex to update the correct group
      const targetIndex = groupIndex !== undefined ? groupIndex : currentIndex;
      
      // Update the group with suggestion
      setTransactionGroups(prev => prev.map((g, idx) => 
        idx === targetIndex ? { ...g, suggestion: result } : g
      ));

      // Pre-fill form from AI suggestion if available (only if this is the current group)
      if (targetIndex === currentIndex) {
        if (result.category_type) {
          setCategoryType(result.category_type);
          setSubcategory(result.subcategory || '');
          setDetailSource(result.detail_source || '');
        } else {
          // Default based on account type and amount (credit cards have inverted sign)
          setCategoryType(getDefaultCategory(tx));
        }
      }
    } catch (error) {
      console.error('Error classifying transaction:', error);
      toast({
        title: 'AI Classification Error',
        description: 'Failed to get AI suggestions. You can still categorize manually.',
        variant: 'destructive',
      });
    } finally {
      setClassifying(false);
    }
  };

  const handleSave = async () => {
    if (!subcategory) {
      toast({
        title: 'Subcategory Required',
        description: 'Please select or enter a subcategory for this transaction',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      const currentGroup = transactionGroups[currentIndex];
      const txIds = currentGroup.transactions.map(tx => tx.id);

      // Update ALL transactions in this group with category_type
      // IMPORTANT: This sets category_type, which filters them out of future training queries
      console.log(`Saving ${txIds.length} transactions with category_type: ${categoryType}, subcategory: ${subcategory}`);
      
      const updatePayload: Record<string, unknown> = {
        category_type: categoryType,
        subcategory: subcategory,
        detail_source: detailSource || null,
        auto_categorized: false,
        needs_review: false,
      };
      const { data: updateData, error: updateError } = await supabase
        .from('bank_transactions')
        .update(updatePayload)
        .in('id', txIds)
        .select('id, category_type'); // Return updated records to verify

      if (subcategory === 'payroll') {
        await supabase
          .from('bank_transactions')
          .update({ payroll_assignment_status: 'unassigned' })
          .in('id', txIds)
          .neq('payroll_assignment_status', 'assigned');
      }

      if (updateError) throw updateError;
      
      console.log(`Successfully updated transactions:`, updateData?.length, 'records updated');

      // Save custom detail_source to database if it's a custom value (not in default options)
      if (detailSource && detailSource.trim() && subcategory) {
        try {
          // Check if it's a default option
          const defaultOptions = [
            'platform', 'cash', 'check', 'wire', 'collected', 'remitted',
            'federal', 'state', 'local', 'bank', 'investment',
            'w2', 'tax', '1099', 'base', 'utilities', 'percentage', 'cam',
            'loan', 'credit_card', 'loc', 'doordash', 'ubereats', 'grubhub',
            'toast', 'square', 'stripe', 'internal', 'external',
            'principal', 'interest', 'advance', 'draw', 'contribution', 'distribution'
          ];
          const isDefaultOption = defaultOptions.some(opt => 
            opt.toLowerCase() === detailSource.trim().toLowerCase()
          );
          
          // If not a default option, save to custom_detail_values table
          if (!isDefaultOption) {
            const { error: detailError } = await supabase
              .from('custom_detail_values')
              .upsert({
                company_id: companyId,
                subcategory: subcategory.toLowerCase(),
                detail_value: detailSource.trim(),
                updated_at: new Date().toISOString(),
              }, {
                onConflict: 'company_id,subcategory,detail_value',
              });
            
            if (detailError && detailError.code !== '23505') {
              // Log but don't fail the transaction save if detail save fails
              console.warn('Error saving custom detail:', detailError);
            }
          }
        } catch (error) {
          // Log but don't fail the transaction save
          console.warn('Error saving custom detail value:', error);
        }
      }

      // Generate rule from the first transaction so future imports use this mapping
      const firstTx = currentGroup.transactions[0];
      try {
        await generateRuleFromTransaction(
          {
            id: firstTx.id,
            transaction_date: firstTx.transaction_date,
            amount: firstTx.amount,
            description: firstTx.description || '',
            merchant_name: firstTx.merchant_name,
            category_type: categoryType,
            subcategory: subcategory,
            detail_source: detailSource || undefined,
            location_id: firstTx.location_id || undefined,
          },
          companyId,
          firstTx.account_id
        );
      } catch (ruleErr) {
        console.warn('Onboarding: failed to create rule from transaction', firstTx.id, ruleErr);
        // Transaction update already succeeded; don't fail the whole save
      }

      // Mark ONLY the current group as trained - never mark all groups
      // This prevents the auto-completion glitch where all groups get marked as trained
      const updatedGroups = transactionGroups.map((g, idx) => 
        idx === currentIndex ? { ...g, isTrained: true } : g
      );
      setTransactionGroups(updatedGroups);
      
      // Log for debugging - should only mark one group at a time
      console.log(`Marked group ${currentIndex} as trained. Total groups: ${transactionGroups.length}, Trained: ${updatedGroups.filter(g => g.isTrained).length}`);

      toast({
        title: 'Saved!',
        description: `Categorized ${currentGroup.transactions.length} transaction${currentGroup.transactions.length > 1 ? 's' : ''}`,
      });

      // Move to next untrained group using updated state
      goToNextUntrainedWithState(updatedGroups);

    } catch (error: any) {
      console.error('Error saving:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    goToNextUntrained();
  };

  const goToNextUntrained = async () => {
    // Use the current state from transactionGroups
    goToNextUntrainedWithState(transactionGroups);
  };

  const goToNextUntrainedWithState = async (groups: TransactionGroup[]) => {
    // Find next untrained group using the provided state
    const nextIndex = groups.findIndex((g, idx) => idx > currentIndex && !g.isTrained);
    
    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
      const nextGroup = groups[nextIndex];
      const tx = nextGroup.transactions[0];
      
      // Check if any transaction in the group has saved values
      const savedDetailSource = nextGroup.transactions.find(t => t.detail_source)?.detail_source || null;
      const savedSubcategory = nextGroup.transactions.find(t => t.subcategory)?.subcategory || null;
      const savedCategoryType = nextGroup.transactions.find(t => t.category_type)?.category_type || null;
      
      // Use saved values if available, otherwise use suggestion, otherwise default
      if (savedCategoryType) {
        setCategoryType(savedCategoryType);
        setSubcategory(savedSubcategory || '');
        setDetailSource(savedDetailSource || '');
      } else if (nextGroup.suggestion?.category_type) {
        setCategoryType(nextGroup.suggestion.category_type);
        setSubcategory(nextGroup.suggestion.subcategory || '');
        setDetailSource(nextGroup.suggestion.detail_source || '');
      } else {
        // Default based on account type and amount (credit cards have inverted sign)
        setCategoryType(getDefaultCategory(tx));
        setSubcategory(savedSubcategory || '');
        setDetailSource(savedDetailSource || '');
        // Always try to get AI suggestion if we don't have one yet
        if (!nextGroup.suggestion) {
          await classifyCurrentTransaction(nextGroup, nextIndex);
        }
      }
      setShowSimilar(false);
      // Reset ungroup mode when navigating
      setUngroupMode(false);
      setSelectedTransactionsForUngroup(new Set());
      setExpandedDescriptions(new Set());
    }
    // If nextIndex === -1, no more untrained groups - the component will re-render
    // and show the "Training Complete!" screen automatically
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      const prevGroup = transactionGroups[currentIndex - 1];
      const tx = prevGroup.transactions[0];
      
      // Check if any transaction in the group has saved values
      const savedDetailSource = prevGroup.transactions.find(t => t.detail_source)?.detail_source || null;
      const savedSubcategory = prevGroup.transactions.find(t => t.subcategory)?.subcategory || null;
      const savedCategoryType = prevGroup.transactions.find(t => t.category_type)?.category_type || null;
      
      // Use saved values if available, otherwise use suggestion, otherwise default
      if (savedCategoryType) {
        setCategoryType(savedCategoryType);
        setSubcategory(savedSubcategory || '');
        setDetailSource(savedDetailSource || '');
      } else if (prevGroup.suggestion?.category_type) {
        setCategoryType(prevGroup.suggestion.category_type);
        setSubcategory(prevGroup.suggestion.subcategory || '');
        setDetailSource(prevGroup.suggestion.detail_source || '');
      } else {
        setCategoryType(getDefaultCategory(tx));
        setSubcategory(savedSubcategory || '');
        setDetailSource(savedDetailSource || '');
      }
      setShowSimilar(false);
      // Reset ungroup mode when navigating
      setUngroupMode(false);
      setSelectedTransactionsForUngroup(new Set());
      setExpandedDescriptions(new Set());
    }
  };

  const goToNext = () => {
    if (currentIndex < transactionGroups.length - 1) {
      setCurrentIndex(currentIndex + 1);
      const nextGroup = transactionGroups[currentIndex + 1];
      const tx = nextGroup.transactions[0];
      
      // Check if any transaction in the group has saved values
      const savedDetailSource = nextGroup.transactions.find(t => t.detail_source)?.detail_source || null;
      const savedSubcategory = nextGroup.transactions.find(t => t.subcategory)?.subcategory || null;
      const savedCategoryType = nextGroup.transactions.find(t => t.category_type)?.category_type || null;
      
      // Use saved values if available, otherwise use suggestion, otherwise default
      if (savedCategoryType) {
        setCategoryType(savedCategoryType);
        setSubcategory(savedSubcategory || '');
        setDetailSource(savedDetailSource || '');
      } else if (nextGroup.suggestion?.category_type) {
        setCategoryType(nextGroup.suggestion.category_type);
        setSubcategory(nextGroup.suggestion.subcategory || '');
        setDetailSource(nextGroup.suggestion.detail_source || '');
      } else {
        // Default based on account type and amount (credit cards have inverted sign)
        setCategoryType(getDefaultCategory(tx));
        setSubcategory(savedSubcategory || '');
        setDetailSource(savedDetailSource || '');
        classifyCurrentTransaction(nextGroup);
      }
      setShowSimilar(false);
      // Reset ungroup mode when navigating
      setUngroupMode(false);
      setSelectedTransactionsForUngroup(new Set());
      setExpandedDescriptions(new Set());
    }
  };

  const handleUseSuggestion = () => {
    const currentGroup = transactionGroups[currentIndex];
    if (currentGroup?.suggestion) {
      // Use full suggestion including category type
      if (currentGroup.suggestion.category_type) {
        setCategoryType(currentGroup.suggestion.category_type);
      }
      setSubcategory(currentGroup.suggestion.subcategory || '');
      setDetailSource(currentGroup.suggestion.detail_source || '');
    }
  };

  // Toggle description expansion
  const toggleDescriptionExpansion = (txId: string) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(txId)) {
        newSet.delete(txId);
      } else {
        newSet.add(txId);
      }
      return newSet;
    });
  };

  // Toggle transaction selection for ungroup
  const toggleTransactionSelection = (txId: string) => {
    setSelectedTransactionsForUngroup(prev => {
      const newSet = new Set(prev);
      if (newSet.has(txId)) {
        newSet.delete(txId);
      } else {
        newSet.add(txId);
      }
      return newSet;
    });
  };

  // Handle ungroup action
  const handleUngroup = () => {
    if (selectedTransactionsForUngroup.size === 0) {
      toast({
        title: 'No transactions selected',
        description: 'Please select transactions to ungroup',
        variant: 'destructive',
      });
      return;
    }

    const currentGroup = transactionGroups[currentIndex];
    const transactionsToUngroup = currentGroup.transactions.filter(tx => 
      selectedTransactionsForUngroup.has(tx.id)
    );
    const transactionsToKeep = currentGroup.transactions.filter(tx => 
      !selectedTransactionsForUngroup.has(tx.id)
    );

    if (transactionsToKeep.length === 0) {
      toast({
        title: 'Cannot ungroup all',
        description: 'At least one transaction must remain in the group',
        variant: 'destructive',
      });
      return;
    }

    // Update current group to keep remaining transactions
    const updatedGroups = [...transactionGroups];
    updatedGroups[currentIndex] = {
      ...currentGroup,
      transactions: transactionsToKeep,
      descriptionPattern: transactionsToKeep[0]?.description 
        ? normalizeDescription(transactionsToKeep[0].description) 
        : currentGroup.descriptionPattern,
    };

    // Create ONE new group for all ungrouped transactions
    const firstUngroupedTx = transactionsToUngroup[0];
    const ungroupedPattern = firstUngroupedTx?.description 
      ? normalizeDescription(firstUngroupedTx.description) 
      : 'Ungrouped Transactions';
    
    // isRevenue: Bank = positive is revenue; Credit card = negative is revenue (money in)
    const isRevenue = firstUngroupedTx
      ? firstUngroupedTx.is_credit_card
        ? firstUngroupedTx.amount < 0
        : firstUngroupedTx.amount >= 0
      : false;
    const newGroup: TransactionGroup = {
      descriptionPattern: ungroupedPattern,
      transactions: transactionsToUngroup,
      isTrained: false,
      isRevenue,
    };

    // Insert new group after current index
    updatedGroups.splice(currentIndex + 1, 0, newGroup);

    setTransactionGroups(updatedGroups);
    setSelectedTransactionsForUngroup(new Set());
    setUngroupMode(false);
    setExpandedDescriptions(new Set());

    toast({
      title: 'Transactions ungrouped',
      description: `${transactionsToUngroup.length} transaction(s) moved to a new group`,
    });
  };

  // Helper function to normalize description (for ungroup logic)
  const normalizeDescription = (desc: string): string => {
    return desc
      .toLowerCase()
      .replace(/[0-9#*]+/g, '')
      .replace(/\s+/g, ' ')
      .replace(/[^a-z\s]/g, '')
      .trim()
      .substring(0, 50);
  };

  // Stats
  const totalGroups = transactionGroups.length;
  const trainedGroups = transactionGroups.filter(g => g.isTrained).length;
  const totalTransactions = transactionGroups.reduce((sum, g) => sum + g.transactions.length, 0);
  const trainedTransactions = transactionGroups
    .filter(g => g.isTrained)
    .reduce((sum, g) => sum + g.transactions.length, 0);
  const remainingGroups = totalGroups - trainedGroups;

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-40" />
            </div>
            <Skeleton className="h-2 w-full" />
          </CardContent>
        </Card>
        <div className="flex items-center justify-between">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-9 w-20" />
        </div>
        <Card className="border-2">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-3">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-4 w-40" />
              </div>
              <Skeleton className="h-6 w-24" />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-20 w-full" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
            </div>
          </CardContent>
        </Card>
        <div className="flex items-center justify-between">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-48" />
        </div>
      </div>
    );
  }

  // No transactions - only show after data has loaded to prevent premature completion
  if (dataLoaded && transactionGroups.length === 0) {
    return (
      <div className="space-y-6">
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="text-center py-8 space-y-4">
              <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold mb-2 text-lg">All Caught Up!</h4>
                <p className="text-muted-foreground">No uncategorized transactions remaining.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onPrevious}>← Previous Step</Button>
          <Button onClick={onNext}>
            Continue to Review
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // All trained - only show after data has loaded
  if (dataLoaded && remainingGroups === 0) {
    return (
      <div className="space-y-6">
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="text-center py-8 space-y-4">
              <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold mb-2 text-lg">Training Complete!</h4>
                <p className="text-muted-foreground">
                  You've categorized {trainedTransactions} transactions across {trainedGroups} unique patterns.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onPrevious}>← Previous Step</Button>
          <Button onClick={onNext}>
            Continue to Review
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  const currentGroup = transactionGroups[currentIndex];
  const currentTx = currentGroup.transactions[0];
  const similarCount = currentGroup.transactions.length;

  const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const handleImportTransactions = async () => {
    if (!trainingWindowStart || !trainingWindowEnd) {
      toast({
        title: 'Date range required',
        description: 'Please select a date range before importing',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Import started',
      description: 'Please go back to Step 2 to import transactions with the selected date range.',
    });
  };

  return (
    <div className="space-y-6">
      {/* Date Range Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Select Date Range
          </CardTitle>
          <CardDescription>
            Choose the time period to fetch transactions from your bank accounts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="training-start" className="text-sm font-medium">From Date</Label>
              <Input
                id="training-start"
                type="date"
                value={trainingWindowStart}
                onChange={(e) => setTrainingWindowStart(e.target.value)}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="training-end" className="text-sm font-medium">To Date</Label>
              <Input
                id="training-end"
                type="date"
                value={trainingWindowEnd}
                onChange={(e) => setTrainingWindowEnd(e.target.value)}
                className="h-11"
              />
            </div>
          </div>
          
          <Alert className="bg-blue-50 border-blue-200">
            <Calendar className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Recommended:</strong> Select at least 1 month of transaction history for better training results.
              {trainingWindowStart && trainingWindowEnd && (
                <> Current selection: {formatDisplayDate(trainingWindowStart)} → {formatDisplayDate(trainingWindowEnd)}</>
              )}
            </AlertDescription>
          </Alert>

          <Button
            onClick={handleImportTransactions}
            disabled={importing || !trainingWindowStart || !trainingWindowEnd}
            className="w-full"
            size="lg"
          >
            {importing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Import Transactions
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Header */}
      <div>
        <h3 className="text-xl font-semibold mb-2">Train AI on Your Transactions</h3>
        <p className="text-muted-foreground">
          Categorize one transaction and the AI will learn to automatically categorize similar ones.
        </p>
      </div>

      {/* Progress Bar */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-blue-600" />
              <span className="font-medium">Training Progress</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {trainedGroups} of {totalGroups} patterns • {trainedTransactions} transactions categorized
            </span>
          </div>
          <Progress value={(trainedGroups / totalGroups) * 100} className="h-2" />
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={goToPrevious}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <span className="text-sm text-muted-foreground">
          Pattern {currentIndex + 1} of {totalGroups}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={goToNext}
          disabled={currentIndex === totalGroups - 1}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Main Transaction Card */}
      <Card className={cn(
        "border-2 transition-colors",
        currentGroup.isTrained ? "border-emerald-300 bg-emerald-50/30" : "border-primary/30"
      )}>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg flex items-center gap-2">
                {/* For credit cards, positive = expense (red), negative = payment (green) */}
                {/* For regular accounts, positive = revenue (green), negative = expense (red) */}
                {(() => {
                  const isExpense = currentTx.is_credit_card 
                    ? currentTx.amount >= 0  // CC: positive = charge = expense
                    : currentTx.amount < 0;  // Bank: negative = withdrawal = expense
                  return isExpense 
                    ? <TrendingDown className="h-5 w-5 text-red-600" />
                    : <TrendingUp className="h-5 w-5 text-emerald-600" />;
                })()}
                <span className={cn(
                  "text-2xl font-bold",
                  // Color based on whether it's an expense or revenue
                  currentTx.is_credit_card 
                    ? (currentTx.amount >= 0 ? "text-red-600" : "text-emerald-600")  // CC: + = expense, - = payment
                    : (currentTx.amount >= 0 ? "text-emerald-600" : "text-red-600")  // Bank: + = revenue, - = expense
                )}>
                  {currentTx.amount >= 0 ? '+' : ''}${Math.abs(currentTx.amount).toFixed(2)}
                </span>
              </CardTitle>
              <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                {currentTx.account_name && (
                  <Badge 
                    variant="outline" 
                    className={cn(
                      "text-xs font-medium",
                      currentTx.account_name.toLowerCase().includes('checking') && "bg-blue-50 border-blue-300 text-blue-700",
                      currentTx.account_name.toLowerCase().includes('savings') && "bg-green-50 border-green-300 text-green-700",
                      (currentTx.account_name.toLowerCase().includes('credit') || currentTx.account_name.toLowerCase().includes('cc')) && "bg-purple-50 border-purple-300 text-purple-700",
                      currentTx.account_name.toLowerCase().includes('business') && "bg-orange-50 border-orange-300 text-orange-700",
                      !currentTx.account_name.toLowerCase().includes('checking') && 
                      !currentTx.account_name.toLowerCase().includes('savings') && 
                      !currentTx.account_name.toLowerCase().includes('credit') &&
                      !currentTx.account_name.toLowerCase().includes('cc') &&
                      !currentTx.account_name.toLowerCase().includes('business') && "bg-slate-50 border-slate-300 text-slate-700"
                    )}
                  >
                    <CreditCard className="h-3 w-3 mr-1" />
                    {currentTx.account_name}
                  </Badge>
                )}
                <span className="text-muted-foreground">
                  {new Date(currentTx.transaction_date).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Suggested category badge */}
              <Badge variant="outline" className="text-muted-foreground">
                Suggested: {getDefaultCategory(currentTx) === 'revenue' ? 'Revenue' : getDefaultCategory(currentTx) === 'expenses' ? 'Expenses' : 'Balance Sheet'}
              </Badge>
              {currentGroup.isTrained && (
                <Badge className="bg-emerald-500">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Trained
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Description */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Full Description</Label>
            <div className="bg-muted/50 p-4 rounded-lg font-mono text-sm">
              {currentTx.description}
            </div>
          </div>

          {/* Similar Transactions Alert */}
          {similarCount > 1 && (
            <Alert className="border-blue-200 bg-blue-50">
              <Layers className="h-4 w-4 text-blue-600" />
              <AlertDescription className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span>
                    <strong className="text-blue-700">{similarCount} transactions</strong> match this description pattern. 
                    Categorizing this one will apply to all of them!
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-4"
                    onClick={() => {
                      setShowSimilar(!showSimilar);
                      // Reset ungroup mode when hiding
                      if (showSimilar) {
                        setUngroupMode(false);
                        setSelectedTransactionsForUngroup(new Set());
                        setExpandedDescriptions(new Set());
                      }
                    }}
                  >
                    {showSimilar ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                    {showSimilar ? 'Hide' : 'Show Similar'}
                  </Button>
                </div>
                {/* Show which accounts are involved */}
                {(() => {
                  const accountCounts = currentGroup.transactions.reduce((acc, tx) => {
                    const accountName = tx.account_name || 'Unknown';
                    acc[accountName] = (acc[accountName] || 0) + 1;
                    return acc;
                  }, {} as Record<string, number>);
                  const accountNames = Object.keys(accountCounts);
                  
                  if (accountNames.length > 1) {
                    return (
                      <div className="flex items-center gap-2 text-xs text-blue-600">
                        <CreditCard className="h-3 w-3" />
                        <span>From {accountNames.length} accounts:</span>
                        {accountNames.map((name, idx) => (
                          <Badge 
                            key={name} 
                            variant="outline" 
                            className={cn(
                              "text-xs",
                              name.toLowerCase().includes('checking') && "bg-blue-100 border-blue-300 text-blue-700",
                              name.toLowerCase().includes('savings') && "bg-green-100 border-green-300 text-green-700",
                              (name.toLowerCase().includes('credit') || name.toLowerCase().includes('cc')) && "bg-purple-100 border-purple-300 text-purple-700",
                              name.toLowerCase().includes('business') && "bg-orange-100 border-orange-300 text-orange-700",
                              !name.toLowerCase().includes('checking') && 
                              !name.toLowerCase().includes('savings') && 
                              !name.toLowerCase().includes('credit') &&
                              !name.toLowerCase().includes('cc') &&
                              !name.toLowerCase().includes('business') && "bg-slate-100 border-slate-300 text-slate-700"
                            )}
                          >
                            {name} ({accountCounts[name]})
                          </Badge>
                        ))}
                      </div>
                    );
                  }
                  return null;
                })()}
              </AlertDescription>
            </Alert>
          )}

          {/* Similar Transactions Table */}
          {showSimilar && similarCount > 1 && (
            <Card className="border-dashed">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Similar Transactions ({similarCount})</CardTitle>
                  <Button
                    variant={ungroupMode ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (ungroupMode) {
                        // Cancel ungroup mode
                        setUngroupMode(false);
                        setSelectedTransactionsForUngroup(new Set());
                      } else {
                        // Enter ungroup mode
                        setUngroupMode(true);
                      }
                    }}
                    className="h-7 text-xs"
                  >
                    <Split className="h-3 w-3 mr-1" />
                    {ungroupMode ? 'Cancel' : 'Ungroup'}
                  </Button>
                </div>
                {ungroupMode && (
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      size="sm"
                      onClick={handleUngroup}
                      disabled={selectedTransactionsForUngroup.size === 0}
                      className="h-7 text-xs"
                    >
                      Ungroup Selected ({selectedTransactionsForUngroup.size})
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Select transactions to separate into different groups
                    </span>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {ungroupMode && (
                          <TableHead className="text-xs w-10">
                            <Checkbox
                              checked={selectedTransactionsForUngroup.size === currentGroup.transactions.length}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedTransactionsForUngroup(
                                    new Set(currentGroup.transactions.map(tx => tx.id))
                                  );
                                } else {
                                  setSelectedTransactionsForUngroup(new Set());
                                }
                              }}
                            />
                          </TableHead>
                        )}
                        <TableHead className="text-xs w-24">Account</TableHead>
                        <TableHead className="text-xs w-20">Date</TableHead>
                        <TableHead className="text-xs w-24">Amount</TableHead>
                        <TableHead className="text-xs">Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentGroup.transactions.map((tx) => {
                        const isExpanded = expandedDescriptions.has(tx.id);
                        const description = tx.description || '';
                        const truncatedDescription = description.length > 60 
                          ? description.substring(0, 60) + '...' 
                          : description;
                        
                        return (
                          <TableRow key={tx.id}>
                            {ungroupMode && (
                              <TableCell className="py-2">
                                <Checkbox
                                  checked={selectedTransactionsForUngroup.has(tx.id)}
                                  onCheckedChange={() => toggleTransactionSelection(tx.id)}
                                />
                              </TableCell>
                            )}
                            <TableCell className="text-xs py-2 whitespace-nowrap">
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-xs font-medium",
                                  // Color-code by account to make it easy to identify
                                  tx.account_name?.toLowerCase().includes('checking') && "bg-blue-50 border-blue-300 text-blue-700",
                                  tx.account_name?.toLowerCase().includes('savings') && "bg-green-50 border-green-300 text-green-700",
                                  tx.account_name?.toLowerCase().includes('credit') || tx.account_name?.toLowerCase().includes('cc') && "bg-purple-50 border-purple-300 text-purple-700",
                                  tx.account_name?.toLowerCase().includes('business') && "bg-orange-50 border-orange-300 text-orange-700",
                                  !tx.account_name?.toLowerCase().includes('checking') && 
                                  !tx.account_name?.toLowerCase().includes('savings') && 
                                  !tx.account_name?.toLowerCase().includes('credit') &&
                                  !tx.account_name?.toLowerCase().includes('cc') &&
                                  !tx.account_name?.toLowerCase().includes('business') && "bg-slate-50 border-slate-300 text-slate-700"
                                )}
                              >
                                {tx.account_name || 'Unknown'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs py-2 whitespace-nowrap">
                              {new Date(tx.transaction_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </TableCell>
                            <TableCell className={cn(
                              "text-xs py-2 font-mono whitespace-nowrap",
                              // Color based on whether it's an expense or revenue
                              // Credit cards: positive = expense (red), negative = payment (green)
                              // Regular accounts: positive = revenue (green), negative = expense (red)
                              tx.is_credit_card 
                                ? (tx.amount >= 0 ? "text-red-600" : "text-emerald-600")
                                : (tx.amount >= 0 ? "text-emerald-600" : "text-red-600")
                            )}>
                              ${Math.abs(tx.amount).toFixed(2)}
                            </TableCell>
                            <TableCell className="text-xs py-2">
                              <div className="space-y-1">
                                <div className="flex items-start gap-2">
                                  <span className={cn(
                                    "font-mono break-all",
                                    !isExpanded && "line-clamp-2"
                                  )}>
                                    {isExpanded ? description : truncatedDescription}
                                  </span>
                                  {description.length > 60 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 flex-shrink-0"
                                      onClick={() => toggleDescriptionExpansion(tx.id)}
                                    >
                                      {isExpanded ? (
                                        <ChevronUp className="h-3 w-3" />
                                      ) : (
                                        <ChevronDown className="h-3 w-3" />
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Suggestion */}
          {classifying ? (
            <div className="flex items-center gap-3 p-4 bg-primary/5 rounded-lg border border-primary/20">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm">AI is analyzing this transaction...</span>
            </div>
          ) : currentGroup.suggestion?.category_type ? (
            <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-purple-900">AI Suggestion</p>
                    <p className="text-sm text-purple-700">
                      <span className="font-semibold">{currentGroup.suggestion.category_type}</span>
                      {' → '}
                      <span className="font-semibold">{currentGroup.suggestion.subcategory}</span>
                      {currentGroup.suggestion.detail_source && (
                        <>
                          {' → '}
                          <span>{currentGroup.suggestion.detail_source}</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={
                    currentGroup.suggestion.confidence >= 0.85 ? "default" :
                    currentGroup.suggestion.confidence >= 0.7 ? "secondary" : "outline"
                  }>
                    {Math.round(currentGroup.suggestion.confidence * 100)}% confidence
                  </Badge>
                  <Button size="sm" onClick={handleUseSuggestion}>
                    <Zap className="h-4 w-4 mr-1" />
                    Use
                  </Button>
                </div>
              </div>
              {currentGroup.suggestion.method && (
                <p className="text-xs text-muted-foreground mt-2">
                  Based on: {currentGroup.suggestion.method === 'rule' ? 'Existing rule' : 
                            currentGroup.suggestion.method === 'ml' ? 'Machine learning' :
                            currentGroup.suggestion.method === 'hybrid' ? 'Rule + ML' : 'Pattern matching'}
                </p>
              )}
            </div>
          ) : (
            <Alert variant="default" className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                No AI suggestion available. Please categorize this transaction manually.
              </AlertDescription>
            </Alert>
          )}

          {/* Categorization Form */}
          <div className="space-y-4 pt-4 border-t">
            <div className="grid grid-cols-3 gap-4">
              {/* Category Type - Editable dropdown */}
              <div className="space-y-2">
                <Label>Category Type *</Label>
                <Select value={categoryType} onValueChange={(v: 'revenue' | 'expenses' | 'balance_sheet') => {
                  setCategoryType(v);
                  // Reset subcategory when category type changes
                  setSubcategory('');
                  setDetailSource('');
                }}>
                  <SelectTrigger>
                    <div className="flex items-center gap-2 flex-1">
                      <List className="h-4 w-4 opacity-50" />
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-emerald-600" />
                        Revenue
                      </div>
                    </SelectItem>
                    <SelectItem value="expenses">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="h-4 w-4 text-red-600" />
                        Expenses
                      </div>
                    </SelectItem>
                    <SelectItem value="balance_sheet">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-blue-600" />
                        Balance Sheet
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Default: {(() => {
                    const defaultCat = getDefaultCategory(currentTx);
                    return defaultCat === 'revenue' ? 'Revenue' : defaultCat === 'expenses' ? 'Expenses' : 'Balance Sheet';
                  })()} (based on amount)
                </p>
              </div>

              {/* Subcategory - Dropdown with custom input */}
              <div className="space-y-2">
                <Label>Subcategory *</Label>
                <SubcategoryCombobox
                  categoryType={categoryType}
                  value={subcategory}
                  onValueChange={(value) => {
                    setSubcategory(value);
                    // Reset detail source when subcategory changes
                    setDetailSource('');
                  }}
                  placeholder="Select or type..."
                />
              </div>

              {/* Detail */}
              <div className="space-y-2">
                <Label>Detail</Label>
                <DetailSourceCombobox
                  subcategory={subcategory}
                  value={detailSource}
                  onValueChange={setDetailSource}
                  placeholder="Select or type..."
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={handleSkip} disabled={saving}>
          Skip for Now
        </Button>
        <Button onClick={handleSave} disabled={saving || !subcategory}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Save & Categorize {similarCount > 1 ? `${similarCount} Transactions` : 'Transaction'}
            </>
          )}
        </Button>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t">
        <Button variant="outline" onClick={onPrevious}>
          ← Previous Step
        </Button>
        <Button onClick={onNext}>
          {remainingGroups > 0 ? `Continue (${remainingGroups} remaining)` : 'Continue to Review'}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};
